import React, {createContext, ReactNode, useCallback, useEffect, useRef, useState} from "react"
import {
    EmailAuthProvider,
    GoogleAuthProvider,
    linkWithCredential,
    reauthenticateWithCredential,
    reauthenticateWithPopup,
    sendPasswordResetEmail,
    signInWithCustomToken,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut as firebaseSignOut,
    updatePassword,
    User as FirebaseUser
} from "@firebase/auth"
import {AuthError} from "firebase/auth"
import {useCookies} from "react-cookie"
import {auth} from "../firebase"
import {useApiService} from "../hooks/useApiService"
import {User, UserFormData} from "../types/user"
import {currentUser as fetchCurrentUser, logout, openSession, register as registerAccount, updateProfile} from "../services/api/auth"
import {clearReferralCode, storedReferralCode} from "../services/referral"
import AuthDialog from "../components/AuthDialog"
import {AuthMode} from "../components/AuthForm"

export const SESSION_COOKIE = "scorevision_user"

/** How long the session cookie mirrors the Firebase session for */
const SESSION_DURATION_MS = 36 * 3600 * 1000

export type AuthContextState = {
    firebaseUser?: FirebaseUser | null
    user?: User | null
    /** True until the stored Firebase session has been resolved, so guards do not redirect too early */
    loading: boolean
    /** True while a sign in, signup or profile call is running */
    submitting: boolean
    /** Google accounts have no password to change */
    isPasswordAccount: boolean
    signIn?: (email: string, password: string) => Promise<void>
    signInWithGoogle?: () => Promise<void>
    register?: (form: UserFormData) => Promise<void>
    signOut?: () => Promise<void>
    /**
     * Asks for an account without leaving the page.
     *
     * Resolves with the account once it is signed in, or with null when the visitor closes the
     * dialog, so a caller can carry on with whatever needed the account:
     *
     * if (await auth.requestSignIn("Sign in to save this analysis")) save()
     */
    requestSignIn?: (reason?: string, initialMode?: AuthMode) => Promise<User | null>
    closeAuthDialog?: () => void
    changeName?: (name: string) => Promise<void>
    changePassword?: (currentPassword: string, newPassword: string) => Promise<void>
    /** Gives an account that signs in with Google a password of its own, next to Google */
    setPassword?: (newPassword: string) => Promise<void>
    resetPassword?: (email: string) => Promise<void>
    refresh?: () => Promise<User | null>
}

/**
 * Firebase error codes carry no wording a user should read, so the ones a sign in screen can
 * actually produce are translated here and everything else falls back to a generic message.
 */
const AUTH_ERROR_MESSAGES: { [code: string]: string } = {
    "auth/invalid-credential": "Invalid email or password",
    "auth/invalid-email": "This email address is not valid",
    "auth/user-disabled": "This account has been disabled",
    "auth/user-not-found": "No account exists for this email",
    "auth/wrong-password": "Invalid email or password",
    "auth/email-already-in-use": "An account already exists with this email",
    "auth/weak-password": "Choose a password of at least 8 characters",
    "auth/requires-recent-login": "Please sign in again before changing your password",
    "auth/provider-already-linked": "This account already has a password",
    "auth/credential-already-in-use": "This email is already used by another account",
    "auth/popup-closed-by-user": "Sign-in cancelled",
    "auth/cancelled-popup-request": "Sign-in cancelled",
    "auth/popup-blocked": "Your browser blocked the sign-in pop-up",
    "auth/account-exists-with-different-credential": "An account already exists with this email using a different sign-in method",
    "auth/network-request-failed": "Network error. Check your connection",
    "auth/operation-not-allowed": "This sign-in method is not enabled for this project"
}

/**
 * Turns anything thrown by Firebase or by the API into a message worth showing.
 *
 * API rejections are already plain objects carrying the server message, so they pass straight
 * through; only Firebase codes need translating.
 */
export const authErrorMessage = (error: any): string =>
    AUTH_ERROR_MESSAGES[(error as AuthError)?.code]
    || error?.message
    || "Something went wrong, please try again"

const hasPasswordProvider = (user?: FirebaseUser | null) =>
    !!user?.providerData?.some(provider => provider.providerId === EmailAuthProvider.PROVIDER_ID)

export const IAuthContext = createContext<AuthContextState>({
    loading: true,
    submitting: false,
    isPasswordAccount: false
})

const AuthContext = ({children}: { children: ReactNode }) => {
    const apiService = useApiService()
    const [, setCookie, removeCookie] = useCookies([SESSION_COOKIE])
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>()
    /**
     * Kept apart from the Firebase user: linking a password changes its providers in place, on the
     * same object, which would not re-render anything reading them from it.
     */
    const [hasPassword, setHasPassword] = useState(false)
    const [user, setUser] = useState<User | null>(null)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [authRequest, setAuthRequest] = useState<{ reason?: string, initialMode?: AuthMode } | null>(null)
    /** Waiting caller of requestSignIn, settled when the dialog either signs someone in or closes */
    const pendingRequest = useRef<((account: User | null) => void) | null>(null)
    /** Account request currently on the wire, shared by every caller that asks meanwhile */
    const inFlightLoad = useRef<Promise<User | null> | null>(null)

    const settleRequest = useCallback((account: User | null) => {
        pendingRequest.current?.(account)
        pendingRequest.current = null
        setAuthRequest(null)
    }, [])

    const requestSignIn = (reason?: string, initialMode?: AuthMode) => new Promise<User | null>(resolve => {
        if (user) {
            resolve(user)
            return
        }

        // A second ask supersedes the first, which would otherwise never settle
        pendingRequest.current?.(null)
        pendingRequest.current = resolve
        setAuthRequest({reason, initialMode})
    })

    /** The dialog is driven by the session rather than by its own form, so any path in closes it */
    useEffect(() => {
        if (user && pendingRequest.current)
            settleRequest(user)
    }, [user, settleRequest])

    /** Last account written to the cookie, so an unchanged one is not written again */
    const rememberedAccount = useRef<string | undefined>(undefined)

    /**
     * Mirrors the session in a cookie.
     *
     * Writing it re-renders every cookie consumer, so an identical account is skipped: rewriting
     * it on each load is what turned a single account fetch into an endless one.
     */
    const rememberSession = useCallback((account: User | null) => {
        const serialized = account ? JSON.stringify(account) : ""

        if (serialized === rememberedAccount.current)
            return

        rememberedAccount.current = serialized

        if (!account) {
            removeCookie(SESSION_COOKIE, {path: "/"})
            return
        }

        setCookie(SESSION_COOKIE, {
            currentUser: account,
            expiresAt: Date.now() + SESSION_DURATION_MS
        }, {
            path: "/",
            expires: new Date(Date.now() + SESSION_DURATION_MS)
        })
    }, [removeCookie, setCookie])

    /**
     * Loads the application account of the signed in Firebase identity.
     *
     * A Firebase identity created outside the signup form (Google) has no account document until
     * the session endpoint provisions one, so a failed lookup is retried through it once before
     * being treated as a real failure.
     */
    const loadAccount = useCallback(async (): Promise<User | null> => {
        // Signing in and the auth state listener both ask for the account: one request answers both
        if (inFlightLoad.current)
            return inFlightLoad.current

        const load = (async () => {
            const client = await apiService.client(true, false)

            try {
                const account = (await fetchCurrentUser(client))?.response?.[0] || null
                rememberSession(account)
                setUser(account)
                return account
            } catch (error) {
                const provisioned = (await openSession(client, storedReferralCode()))?.response?.[0] || null
                clearReferralCode()
                rememberSession(provisioned)
                setUser(provisioned)
                return provisioned
            } finally {
                inFlightLoad.current = null
            }
        })()

        inFlightLoad.current = load
        return load
    }, [apiService, rememberSession])

    // Kept current so the auth state listener below can subscribe once and still call the latest ones
    const loadAccountRef = useRef(loadAccount)
    const rememberSessionRef = useRef(rememberSession)
    loadAccountRef.current = loadAccount
    rememberSessionRef.current = rememberSession

    /**
     * Firebase restores the session from local storage asynchronously, so the account is loaded
     * from this listener rather than on mount, and it also catches the token expiring.
     *
     * Subscribed exactly once, through refs: Firebase replays the current state to every new
     * listener, so re-subscribing on a changed callback identity re-fetched the account, whose
     * cookie changed that identity again.
     */
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (value) => {
            setFirebaseUser(value)
            setHasPassword(hasPasswordProvider(value))

            if (!value) {
                setUser(null)
                rememberSessionRef.current(null)
                setLoading(false)
                return
            }

            try {
                await loadAccountRef.current()
            } catch (error) {
                console.log("Could not load the current account: ", error)
                setUser(null)
            } finally {
                setLoading(false)
            }
        })

        return () => unsubscribe()
    }, [])

    const signIn = async (email: string, password: string) => {
        setSubmitting(true)
        try {
            await signInWithEmailAndPassword(auth, email, password)
            await loadAccount()
        } finally {
            setSubmitting(false)
        }
    }

    const signInWithGoogle = async () => {
        setSubmitting(true)
        try {
            const provider = new GoogleAuthProvider()
            // Without it the browser silently reuses the last Google account, which surprises anyone with two
            provider.setCustomParameters({prompt: "select_account"})

            await signInWithPopup(auth, provider)

            const client = await apiService.client(true, false)
            const account = (await openSession(client, storedReferralCode()))?.response?.[0] || null

            clearReferralCode()
            rememberSession(account)
            setUser(account)
        } finally {
            setSubmitting(false)
        }
    }

    /**
     * Creates the account server side, then signs in with the custom token it returns.
     *
     * The Firebase user is created by the API (it also provisions the account document and the
     * referral rewards), so the browser never creates it itself.
     */
    const register = async (form: UserFormData) => {
        setSubmitting(true)
        try {
            const client = await apiService.client(false, false)
            const created = (await registerAccount(client, form))?.response?.[0]

            if (created?.token)
                await signInWithCustomToken(auth, created.token)
            else
                await signInWithEmailAndPassword(auth, form.email, form.password)

            clearReferralCode()
            await loadAccount()
        } finally {
            setSubmitting(false)
        }
    }

    const signOut = async () => {
        try {
            const client = await apiService.client(true, false)
            await logout(client)
        } catch (error) {
            // The session lives in the Firebase token, so the sign out below is what actually matters
            console.log("Sign out call failed: ", error)
        }

        await firebaseSignOut(auth)
        rememberSession(null)
        setUser(null)
    }

    const changeName = async (name: string) => {
        setSubmitting(true)
        try {
            const client = await apiService.client(true, false)
            const updated = (await updateProfile(client, name))?.response?.[0] || null

            rememberSession(updated)
            setUser(updated)
        } finally {
            setSubmitting(false)
        }
    }

    /**
     * Firebase refuses a password change on a session that is not recent, so the current password
     * is always confirmed first. It doubles as the check that the person at the keyboard is the
     * account owner.
     */
    const changePassword = async (currentPassword: string, newPassword: string) => {
        setSubmitting(true)
        try {
            const current = auth.currentUser

            if (!current?.email)
                throw new Error("This account has no password to change")

            await reauthenticateWithCredential(current, EmailAuthProvider.credential(current.email, currentPassword))
            await updatePassword(current, newPassword)
        } finally {
            setSubmitting(false)
        }
    }

    /**
     * Links an email and password credential to the signed in identity, so an account that came in
     * through Google can also sign in with its email. Firebase refuses the link on a session that is
     * not recent, in which case the Google sign in is confirmed again and the link retried once.
     */
    const setPassword = async (newPassword: string) => {
        setSubmitting(true)
        try {
            const current = auth.currentUser

            if (!current?.email)
                throw new Error("This account has no email to sign in with")

            const credential = EmailAuthProvider.credential(current.email, newPassword)

            try {
                await linkWithCredential(current, credential)
            } catch (error) {
                if ((error as AuthError)?.code !== "auth/requires-recent-login")
                    throw error

                await reauthenticateWithPopup(current, new GoogleAuthProvider())
                await linkWithCredential(current, credential)
            }

            await current.reload()
            setHasPassword(hasPasswordProvider(auth.currentUser))
        } finally {
            setSubmitting(false)
        }
    }

    const resetPassword = async (email: string) => sendPasswordResetEmail(auth, email)

    return <IAuthContext.Provider value={{
        firebaseUser,
        user,
        loading,
        submitting,
        isPasswordAccount: hasPassword,
        signIn,
        signInWithGoogle,
        register,
        signOut,
        changeName,
        changePassword,
        setPassword,
        resetPassword,
        refresh: loadAccount,
        requestSignIn,
        closeAuthDialog: () => settleRequest(null)
    }}>
        {children}

        <AuthDialog open={!!authRequest}
                    reason={authRequest?.reason}
                    initialMode={authRequest?.initialMode}
                    onClose={() => settleRequest(null)}/>
    </IAuthContext.Provider>
}

export default AuthContext
